import React, { memo } from 'react';
import { Skeleton } from '@material-ui/lab';
import { FlagRounded as FlagIcon } from '@material-ui/icons';
import { PRODUCT_TITLE_HEIGHT, INTAKE } from '../../../constants/patientChart.constant';
import { removeXSSAttacks } from '../../../common/helper';
import InProgress from '../InProgress/v1';

export const QAndABackgroundColor = new Map([
  [0, 'bg-white-v3'],
  [1, 'bg-white'],
  [2, 'bg-error-v2'],
]);

export const QuestionAnswerLoading = memo(() => (
  <div className='flex flex-column flex-fill m-b-6'>
    <div className='flex height-40 align-items-center'>
      <Skeleton variant='text' className='width-420' />
    </div>
    <div className='border-top-2-bg-dark-primary-v4 m-b-3'></div>
    <div className={`flex flex-column justify-content-center p-3 min-height-60 ${QAndABackgroundColor.get(0)}`}>
      <div className='flex'>
        <Skeleton variant='text' className='width-165' />
      </div>
      <div className='flex m-t-2'>
        <Skeleton variant='text' className='width-72' />
      </div>
    </div>
    <div className={`flex flex-column justify-content-center p-3 min-height-60 ${QAndABackgroundColor.get(1)}`}>
      <div className='flex'>
        <Skeleton variant='text' className='width-165' />
      </div>
      <div className='flex m-t-2'>
        <Skeleton variant='text' className='width-72' />
      </div>
    </div>
  </div>
));

export const QuestionAnswer = memo(({ orderId = '', patientId = '', QuestionId, QuestionText, AnswerText, AnswerType, Flagged = false, index }) => {
  const handleOpenUpload = () => {
    window.open(
      `/viewuploads/${orderId}/${patientId}/${QuestionId}`,
      '_blank',
      'toolbar=0,location=0,modal=yes,alwaysRaised=yes,directories=0,status=1,menubar=0,titlebar=0,scrollbars=1,resizable=1,left=120,top=200,width=1290,height=780',
    );
  };

  const FlaggedContent = (
    <div className={`flex align-items-center p-l-3 p-r-3 p-t-2 p-b-2 border-bottom-2-bg-lighter-grey ${QAndABackgroundColor.get(2)}`}>
      <div className='flex min-width-30 align-items-center justify-content-flex-start'>
        <FlagIcon fontSize='small' className='color-error' />
      </div>
      <div className='flex flex-column'>
        <div
          className='flex font-roboto font-14 color-dark-primary'
          dangerouslySetInnerHTML={{
            __html: removeXSSAttacks(QuestionText),
          }}
        />
        <div className='flex font-roboto font-14 color-error m-t-2 bold'>{AnswerText}</div>
      </div>
    </div>
  );

  const AnswerIsAnUpload = (
    <div className='flex m-t-2'>
      <div className='font-roboto font-14 underline transform-uppercase bold pointer color-primary' onClick={handleOpenUpload}>
        {AnswerText}
      </div>
    </div>
  );

  const AnswerIsText = <div className='flex font-roboto font-14 color-dark-primary-v2 m-t-2 bold'>{AnswerText}</div>;

  const AnswerComponent = new Map([
    ['upload', AnswerIsAnUpload],
    ['text', AnswerIsText],
  ]);

  const NonFlaggedContent = (
    <div className={`flex flex-column justify-content-center p-l-3 p-r-3 p-t-2 p-b-2 border-bottom-2-bg-lighter-grey ${QAndABackgroundColor.get(index % 2)}`}>
      <div
        className='flex font-roboto font-14 color-dark-primary'
        dangerouslySetInnerHTML={{
          __html: removeXSSAttacks(QuestionText),
        }}
      />
      {
        AnswerComponent.get((AnswerType || 'text').toLowerCase()) || AnswerIsText
      }
    </div>
  );

  const Component = new Map([
    [true, FlaggedContent],
    [false, NonFlaggedContent],
  ]);

  return Component.get(!!Flagged);
});

export const ProductWithQuestionAnswer = memo(({
  index = 0,
  orderId = '',
  patientId = '',
  ProductTitle,
  QuestionAnswers,
}) => {
  const questions = (QuestionAnswers || []);

  const Content = questions.map((questionAnswer, index) => (
    <QuestionAnswer
      key={questionAnswer.QuestionId}
      index={index}
      orderId={orderId}
      patientId={patientId}
      {...questionAnswer}
    />
  ));

  const NoContent = <InProgress doNotDisplayAsTabContent message='No consult data for this product. Please contact Maria Flansbaum at 480-707-4538.' />;

  const Component = new Map([
    [true, Content],
    [false, NoContent],
  ]);

  return (
    <>
      <div className='flex flex-column sticky bg-white' style={{ top: `${index * PRODUCT_TITLE_HEIGHT}px` }}>
        <div className='flex font-22 font-roboto color-dark-primary-v2 bold height-40 align-items-center'>{ProductTitle}</div>
        <div className='border-top-2-bg-dark-primary-v4 m-b-3'></div>
      </div>
      {
        Component.get(!!questions.length)
      }
      <div className='m-b-6'></div>
    </>
  );
});

export const Intake = memo(({
  tabKey = '',
  selectedKey = '',
  data: {
    orderId = '',
    patientId = '',
    intake = INTAKE(),
  },
}) => {
  const { api: { loading = false, called = false }, ProductConsultDetails: products = [] } = intake;

  const Intakes = products.map((product, index) => (<ProductWithQuestionAnswer key={product.ProductId} index={index} orderId={orderId} patientId={patientId} {...product} />));

  const Component = new Map([
    ['false-false', <QuestionAnswerLoading />],
    ['true-true', <QuestionAnswerLoading />],
    ['true-false', Intakes],
  ]);

  const displayClass = tabKey !== selectedKey ? 'd-none' : '';

  return (
    <div className={`flex flex-column flex-fill column-content scroll-y-auto ${displayClass}`}>
      {Component.get(`${called}-${loading}`)}
    </div>
  );
});

export default Intake;
